import { adresseDe } from './consignes'
import type { TypePiece } from './domaine'

// Regroupement visuel des listes (tâches, pièces) : un arbre à deux niveaux, le tiers (ou
// l'émetteur) puis la sorte (même action, même règle, même type de pièce). Rien n'est
// stocké : les clés se calculent sur chaque ligne, l'ordre de la liste est gardé.

export type CleGroupe = { cle: string; libelle: string }

export type NoeudGroupe<T> =
  { element: T } | { cle: string; libelle: string; total: number; enfants: NoeudGroupe<T>[] }

type Paquet<T> = { cle: string | null; libelle: string; elements: T[] }

/**
 * Arbre des éléments selon les niveaux donnés, dans l'ordre de première apparition (une liste
 * triée par urgence garde son ordre, chaque groupe à la place de son premier élément).
 * - un groupe d'un seul élément reste une ligne simple ;
 * - un groupe qui n'a qu'un sous-groupe se fond avec lui (« Magasins U · Mails à trancher ») ;
 * - les éléments sans clé : avec `sansGroupe` (premier niveau), à la fin, dans un groupe de ce
 *   nom s'il y en a au moins deux et qu'il y a autre chose à côté ; sans, à leur place ; dans
 *   les deux cas sous-groupés par les niveaux suivants.
 */
export function grouperEnArbre<T>(
  elements: readonly T[],
  niveaux: readonly ((e: T) => CleGroupe | null)[],
  sansGroupe?: string,
): NoeudGroupe<T>[] {
  const [cleDe, ...suivants] = niveaux
  if (!cleDe) return elements.map((element) => ({ element }))

  const paquets: Paquet<T>[] = []
  const parCle = new Map<string | null, Paquet<T>>()
  for (const e of elements) {
    const c = cleDe(e)
    const cle = c?.cle ?? null
    let p = parCle.get(cle)
    if (!p) {
      p = { cle, libelle: c?.libelle ?? '', elements: [] }
      parCle.set(cle, p)
      paquets.push(p)
    }
    p.elements.push(e)
  }

  const sans = parCle.get(null)
  const envelopper =
    sansGroupe !== undefined &&
    sans !== undefined &&
    sans.elements.length >= 2 &&
    paquets.length > 1

  const noeuds: NoeudGroupe<T>[] = []
  for (const p of paquets) {
    if (p.cle === null) {
      // Sans groupe nommé (niveaux suivants) : à leur place ; sinon à la fin, plus bas.
      if (sansGroupe === undefined) noeuds.push(...grouperEnArbre(p.elements, suivants))
      continue
    }
    if (p.elements.length === 1) {
      noeuds.push({ element: p.elements[0]! })
      continue
    }
    noeuds.push(fondre(p.cle, p.libelle, grouperEnArbre(p.elements, suivants)))
  }
  if (sans && sansGroupe !== undefined) {
    const enfants = grouperEnArbre(sans.elements, suivants)
    if (envelopper)
      noeuds.push({ cle: '', libelle: sansGroupe, total: sans.elements.length, enfants })
    else noeuds.push(...enfants)
  }
  return noeuds
}

function fondre<T>(cle: string, libelle: string, enfants: NoeudGroupe<T>[]): NoeudGroupe<T> {
  const [seul] = enfants
  if (enfants.length === 1 && seul && 'enfants' in seul)
    return {
      cle: `${cle}/${seul.cle}`,
      libelle: `${libelle} · ${seul.libelle}`,
      total: seul.total,
      enfants: seul.enfants,
    }
  return { cle, libelle, total: compter(enfants), enfants }
}

function compter<T>(noeuds: readonly NoeudGroupe<T>[]): number {
  return noeuds.reduce((n, x) => n + ('element' in x ? 1 : x.total), 0)
}

/** Les éléments dans l'ordre de l'écran : celui du parcours une à une depuis la fiche. */
export function elementsDansLOrdre<T>(noeuds: readonly NoeudGroupe<T>[]): T[] {
  return noeuds.flatMap((n) => ('element' in n ? [n.element] : elementsDansLOrdre(n.enfants)))
}

/** Nom d'une sorte d'après un titre : sans ce qui suit « : » ni la période finale entre parenthèses. */
export function libelleDeSorte(titre: string): string {
  const avant = titre.split(' : ')[0] ?? titre
  return avant.replace(/\s*\([^()]*\)\s*$/, '').trim() || titre
}

/** Le nom affiché d'un en-tête « Nom <adresse> », sinon l'adresse. */
export function nomExpediteur(de: string | null): string | null {
  if (!de) return null
  const nom = de
    .replace(/<[^<>]*>/, '')
    .trim()
    .replace(/^"(.*)"$/, '$1')
    .trim()
  return nom || adresseDe(de) || de.trim()
}

// Tâches

export type TacheAGrouper = {
  titre: string
  action: string | null
  echeanceId: string | null
  typeRegle: string | null
  typeGuide: string | null
  /** Titre du guide suivi, pour nommer la sorte d'une tâche libre. */
  guideTitre: string | null
  executantTiersId: string | null
  executantTiers: string | null
  tiersPaiementId: string | null
  tiersPaiement: string | null
  tiersPieceId: string | null
  tiersPiece: string | null
  /** En-tête « De » du mail qui a créé la tâche, s'il est visible. */
  expediteur: string | null
  /** Tiers reconnu à l'adresse de l'expéditeur (`tiers.adresses_mail`). */
  tiersExpediteurId: string | null
  tiersExpediteur: string | null
}

const SORTES_MAIL: Record<string, string> = {
  traiter_mail: 'Mails à trancher',
  verifier_mail: 'Mails à vérifier',
  traiter_demande: 'Demandes reçues par mail',
}

const SORTES_ACTION: Record<string, string> = {
  payer: 'Paiements à faire',
  choisir_rapprochement: 'Rapprochements bancaires',
  verifier_piece: 'Pièces à vérifier',
  choisir_rubrique: 'Pièces à classer',
  decider_reglement: 'Règlements à indiquer',
  indiquer_payeur: 'Factures : qui a payé, à qui refacturer',
  completer_fiche_fournisseur: 'Fiches de fournisseur à compléter',
  factures_manquantes: 'Factures manquantes (commandes)',
  creer_note_frais: 'Notes de frais à créer',
  collecteur: 'Collecteurs',
  justificatifs_a_trancher: 'Justificatifs à choisir',
  justificatifs_a_fournir: 'Justificatifs à fournir',
  justificatifs_a_verifier: 'Justificatifs à vérifier',
}

const tiersCle = (id: string | null, nom: string | null): CleGroupe | null =>
  id ? { cle: `tiers:${id}`, libelle: nom ?? 'Tiers' } : null

/**
 * Groupe et sous-groupe d'une tâche.
 * - tiers : celui à qui elle est confiée, sinon celui de son paiement, de sa pièce, de
 *   l'expéditeur reconnu de son mail, sinon l'expéditeur lui-même (par son adresse) ;
 * - sorte : un mail (par action, et par expéditeur quand le tiers n'est pas lui), une règle
 *   d'échéance et son action, un guide suivi par une tâche libre, une action ; sinon aucune.
 */
export function groupesTache(t: TacheAGrouper): {
  tiers: CleGroupe | null
  sorte: CleGroupe | null
} {
  const adresse = adresseDe(t.expediteur)
  const tiersConnu =
    tiersCle(t.executantTiersId, t.executantTiers) ??
    tiersCle(t.tiersPaiementId, t.tiersPaiement) ??
    tiersCle(t.tiersPieceId, t.tiersPiece)
  const tiers =
    tiersConnu ??
    tiersCle(t.tiersExpediteurId, t.tiersExpediteur) ??
    (adresse ? { cle: `exp:${adresse}`, libelle: nomExpediteur(t.expediteur) ?? adresse } : null)
  return { tiers, sorte: sorteTache(t, tiersConnu !== null ? adresse : null) }
}

function sorteTache(t: TacheAGrouper, expediteurAPart: string | null): CleGroupe | null {
  const mail = t.action ? SORTES_MAIL[t.action] : undefined
  if (mail) {
    if (!expediteurAPart) return { cle: `mail:${t.action}`, libelle: mail }
    const nom = nomExpediteur(t.expediteur) ?? expediteurAPart
    return { cle: `mail:${t.action}:${expediteurAPart}`, libelle: `${mail} (${nom})` }
  }
  if (t.echeanceId && t.typeRegle)
    return { cle: `regle:${t.typeRegle}:${t.action ?? ''}`, libelle: libelleDeSorte(t.titre) }
  if (t.typeGuide)
    return { cle: `guide:${t.typeGuide}`, libelle: t.guideTitre ?? libelleDeSorte(t.titre) }
  if (t.action)
    return {
      cle: `action:${t.action}`,
      libelle: SORTES_ACTION[t.action] ?? libelleDeSorte(t.titre),
    }
  return null
}

// Pièces

const SORTES_PIECE: Record<TypePiece, string> = {
  facture: 'Factures',
  avis: 'Avis',
  courrier: 'Courriers',
  accuse_depot: 'Accusés de dépôt',
  releve: 'Relevés',
  contrat: 'Contrats',
  fiche_paie: 'Fiches de paie',
  autre: 'Autres pièces',
}

/** Groupe (émetteur connu, sinon expéditeur du mail) et sous-groupe (type) d'une pièce. */
export function groupesPiece(p: {
  emetteurId: string | null
  emetteur: string | null
  expediteur: string | null
  type: TypePiece
}): { emetteur: CleGroupe | null; sorte: CleGroupe } {
  const adresse = adresseDe(p.expediteur)
  return {
    emetteur:
      tiersCle(p.emetteurId, p.emetteur) ??
      (adresse ? { cle: `exp:${adresse}`, libelle: nomExpediteur(p.expediteur) ?? adresse } : null),
    sorte: { cle: p.type, libelle: SORTES_PIECE[p.type] },
  }
}
